import {
  generateAttributeFilterOptions,
  handleAdminAreaChangeHelper
} from "@/helpers/datasetAttributeHelper";
import { enrichAdminAreaFilters } from "@/hooks/project/utils";
import { type AdminAreaHierarchy, EAdminAreaLevel } from "@/types/adminArea";
import type {
  DatasetDetailResponse,
  DatasetDetailSchemaResponse,
  DatasetFilter,
  DatasetMetadata
} from "@/types/dataset";
import { EFilterOptionType } from "@/types/dataset";
import {
  type DatasetFilterPoiParameter,
  type DatasetPoiParameter,
  type PoiBrandSearch,
  PoiLevel
} from "@/types/poiParameter";
import { AdminAreaSettings } from "@/types/spatial";

type PoiDatasetGroups = {
  dataSelection: DatasetPoiParameter[][];
  dataAdded: DatasetPoiParameter[];
  dataLocked: DatasetPoiParameter[];
};

type PoiGrouppedByLevel = {
  groups: PoiBrandSearch[];
  categories: PoiBrandSearch[];
  brands: PoiBrandSearch[];
};

/**
 * Splits datasets into subscribed and locked (non-subscribed) groups.
 *
 * @param {DatasetPoiParameter[]} datasets - The full list of datasets to partition
 * @returns {{ subscribed: DatasetPoiParameter[]; dataLocked: DatasetPoiParameter[] }}
 */
const partitionBySubscription = (
  datasets: DatasetPoiParameter[]
): { subscribed: DatasetPoiParameter[]; dataLocked: DatasetPoiParameter[] } => {
  const subscribed: DatasetPoiParameter[] = [];
  const dataLocked: DatasetPoiParameter[] = [];

  for (const ds of datasets) {
    if (ds.is_subscribed) {
      subscribed.push(ds);
    } else {
      dataLocked.push(ds);
    }
  }

  return { subscribed, dataLocked };
};

/**
 * Builds a map of parent dataset IDs to their child datasets.
 *
 * @param {DatasetPoiParameter[]} subscribed - Subscribed datasets to index
 * @returns {Map<string, DatasetPoiParameter[]>} Map from parent_id to its children
 */
const buildChildrenMap = (
  subscribed: DatasetPoiParameter[]
): Map<string, DatasetPoiParameter[]> => {
  const childrenMap = new Map<string, DatasetPoiParameter[]>();

  for (const ds of subscribed) {
    if (ds.parent_id) {
      const siblings = childrenMap.get(ds.parent_id) ?? [];
      childrenMap.set(ds.parent_id, [...siblings, ds]);
    }
  }

  return childrenMap;
};

/**
 * Assembles `dataSelection` groups by grouping datasets that share the same parent_id.
 * Each group contains the parent dataset (if it exists) along with all its children.
 * HOWEVER: If there is only 1 child for a parent_id, it goes directly to dataAdded (not grouped)
 *
 * @param {DatasetPoiParameter[]} subscribed - All subscribed datasets
 * @param {Map<string, DatasetPoiParameter[]>} childrenMap - Map from parent_id to its children
 * @returns {{ dataSelection: DatasetPoiParameter[][]; usedIds: Set<string> }}
 */
const buildSelectionGroups = (
  subscribed: DatasetPoiParameter[],
  childrenMap: Map<string, DatasetPoiParameter[]>
): { dataSelection: DatasetPoiParameter[][]; usedIds: Set<string> } => {
  const dataSelection: DatasetPoiParameter[][] = [];
  const usedIds = new Set<string>();

  // Iterate through each parent_id and its children
  for (const [parentId, children] of childrenMap) {
    // Find the parent dataset in the subscribed list (if it exists)
    const parent = subscribed.find((ds) => ds.dataset_id === parentId);
    // Build group: [parent, ...children] or just children if parent doesn't exist
    const group = parent ? [parent, ...children] : [...children];

    // Skip only if the group has just 1 item (no need for selection)
    if (group.length === 1) {
      continue;
    }

    dataSelection.push(group);
    // Mark all datasets in the group as used
    if (parent) usedIds.add(parent.dataset_id);
    for (const c of children) usedIds.add(c.dataset_id);
  }

  return { dataSelection, usedIds };
};

/**
 * Builds `DatasetFilter[]` from a POI dataset's filter list.
 *
 * For each filter:
 * - `attributeCode` always comes from the `schema_id` schema row (the "Code" row).
 * - All other display fields (label, filterId, attributeId) come from a reverse
 *   lookup: the schema row whose `paired_key_attribute_id === schema_id`. That row
 *   has the clean label without the "Code" suffix (e.g. "Category" not "Category Code").
 * - Fallback: if no such reverse-match exists (e.g. adm area attributes), the
 *   `schema_id` row is used for all fields.
 *
 * Any admin area attribute code (e.g. `adm_area_level_2`, `adm_area_level_4`)
 * is normalised to `adm_area_level_1`, which is the level the stream API expects.
 * Add an `is_multiple_adm_area` flag to indicate that this dataset supports multiple admin area selection.
 *
 * @param filters - Array of `{ schema_id, filters }` from the POI parameter response
 * @param schema  - The dataset's schema rows (from `DatasetMetadata.schema`)
 * @param isMultipleAdminArea - Whether the dataset supports multiple admin area selection
 * @returns An array of `DatasetFilter` ready to pass to `fetchDatasetStream`
 */
const ADM_AREA_LEVEL_PREFIX = "adm_area_level";

export const buildPoiDatasetFilter = (
  filters: DatasetFilterPoiParameter[],
  schema: DatasetDetailSchemaResponse[],
  isMultipleAdminArea?: boolean
) =>
  filters.map((f) => {
    const schemaItem = schema.find((s) => s.id === f.schema_id);

    const rawAttributeCode = schemaItem?.attribute_code ?? "";
    const attributeCode = rawAttributeCode.startsWith(ADM_AREA_LEVEL_PREFIX)
      ? EAdminAreaLevel.LEVEL_1
      : rawAttributeCode;

    // Reverse-lookup: find the row that POINTS TO schema_id via paired_key_attribute_id.
    // That row holds the clean display label (e.g. "Category" vs "Category Code").
    // Falls back to schemaItem when no such row exists (e.g. adm area attributes).
    const pairedSchemaItem = schema.find(
      (s) => s.paired_key_attribute_id === f.schema_id
    );
    const displayItem = pairedSchemaItem ?? schemaItem;

    return {
      filterId: `${displayItem?.id ?? f.schema_id}_${displayItem?.position ?? ""}`,
      filterLabel: displayItem?.attribute_name ?? "",
      filterType: EFilterOptionType.MULTIPLE,
      attributeId: displayItem?.id ?? f.schema_id,
      attributeCode,
      selectedOptions: f.filters,
      paired_key_attribute_id: displayItem?.paired_key_attribute_id ?? null,
      is_multiple_adm_area: isMultipleAdminArea ?? false
    };
  });

/**
 * Builds the exact `DatasetFilter[]` used for a POI dataset's stream request.
 *
 * This is the single source of truth shared by `usePoiConfirmation` for both
 * the confirm/stream flow and the input-rows limit check, so the `/stats`
 * payload can never drift from the stream payload. It reproduces the pipeline:
 *   buildPoiDatasetFilter -> enrichAdminAreaFilters(...).reverse()
 *   -> (admin tree disabled) merge the dropdown admin-area selection.
 *
 * `enrichAdminAreaFilters` processes filters in reverse (each enriched entry is
 * prepended), so the result is reversed back to restore the original order
 * expected by the downstream filter pipeline.
 */
export const buildPoiStreamFilters = ({
  dataset,
  metadata,
  hierarchy,
  isAdminTreeEnabled,
  selectedAdminArea
}: {
  dataset: DatasetPoiParameter;
  metadata: DatasetMetadata;
  hierarchy: AdminAreaHierarchy[];
  isAdminTreeEnabled: boolean;
  selectedAdminArea: AdminAreaSettings | null;
}): DatasetFilter[] => {
  const enrichedFilters = enrichAdminAreaFilters(
    buildPoiDatasetFilter(
      dataset.filters,
      metadata?.schema ?? [],
      isAdminTreeEnabled
    ),
    hierarchy
  ).reverse();

  // When the admin area tree is disabled, apply the dropdown-based admin
  // area selection by merging it into the dataset filters.
  if (isAdminTreeEnabled) return enrichedFilters;

  const filterOptions = generateAttributeFilterOptions(
    metadata as unknown as DatasetDetailResponse
  );
  return handleAdminAreaChangeHelper(
    selectedAdminArea,
    filterOptions,
    enrichedFilters
  );
};

/**
 * Groups datasets into three categories for POI data selection:
 *
 * 1. dataSelection  - Parent datasets with 2 or more children, or datasets with same parent_id (subscribed only)
 * 2. dataAdded      - Subscribed datasets that do NOT belong to any group, or children with only 1 in their parent
 * 3. dataLocked     - All datasets that are NOT subscribed
 *
 * Ensures no dataset appears in more than one category.
 *
 * @param {DatasetPoiParameter[]} datasets - The list of datasets to group
 * @returns {PoiDatasetGroups} An object containing the grouped datasets by status
 */
export const groupDatasetsByStatus = (
  datasets: DatasetPoiParameter[]
): PoiDatasetGroups => {
  const { subscribed, dataLocked } = partitionBySubscription(datasets);
  const childrenMap = buildChildrenMap(subscribed);
  const { dataSelection, usedIds } = buildSelectionGroups(
    subscribed,
    childrenMap
  );
  const dataAdded = subscribed.filter((ds) => !usedIds.has(ds.dataset_id));

  return { dataSelection, dataAdded, dataLocked };
};

/**
 * Groups POI data by hierarchy level (group, category, or brand).
 *
 * @param {PoiBrandSearch[]} data - Array of POI brand search items
 * @returns {PoiGrouppedByLevel} Object containing separated groups, categories, and brands
 */
export const groupPoiByLevel = (data: PoiBrandSearch[]): PoiGrouppedByLevel => {
  return data.reduce<PoiGrouppedByLevel>(
    (acc, item) => {
      const level = item.type ?? PoiLevel.BRAND;

      switch (level) {
        case PoiLevel.BRAND:
          acc.brands.push(item);
          break;
        case PoiLevel.CATEGORY:
          acc.categories.push(item);
          break;
        case PoiLevel.GROUP:
          acc.groups.push(item);
          break;
      }
      return acc;
    },
    {
      groups: [],
      categories: [],
      brands: []
    }
  );
};

export const POILevelLabelMap: Record<
  PoiLevel,
  (item: PoiBrandSearch) => [string, string]
> = {
  [PoiLevel.BRAND]: (item) => [item.brand_name, item.category_name],
  [PoiLevel.CATEGORY]: (item) => [item.category_name, item.group_name],
  [PoiLevel.GROUP]: (item) => [item.group_name, item.industry_name]
};

/**
 * Determines if the global admin area filter is currently selected based on the override setting and the presence of global admin area selections.
 * Logic:
 * 1. If feature flagging the override is not enabled, return false (global filter is not selected).
 * 2. If admin tree is enabled, check if there are any globally selected area IDs. If yes, global filter is selected.
 * 3. If admin tree is not enabled, check if there is a single global admin area setting. If yes, global filter is selected.
 */
export const isGlobalFilterAdminAreaSelected = ({
  isGlobalFilterOverrideEnabled,
  isAdminTreeEnabled,
  globalSelectedAreaIds,
  globalAdminArea
}: {
  isGlobalFilterOverrideEnabled: boolean;
  isAdminTreeEnabled: boolean;
  globalSelectedAreaIds: string[];
  globalAdminArea: AdminAreaSettings | null;
}) => {
  if (!isGlobalFilterOverrideEnabled) return false;
  if (isAdminTreeEnabled) {
    return globalSelectedAreaIds.length > 0;
  }
  return !!globalAdminArea;
};

/**
 * Determines whether the admin area selected in the POI flow matches the
 * currently active global filter. When this returns `true`, datasets added
 * from the POI flow should be linked to the global filter so that subsequent
 * edits to the global filter cascade to those datasets.
 *
 * Tree mode: compares selected ids against global selected ids (set equality).
 * Non-tree mode: compares lv1–lv4 of the selected admin area against the
 * global admin area.
 *
 * Returns `false` when the global filter is not set (lv1 missing AND no
 * selected ids) or when the selections diverge.
 */
export const isAdminAreaSameAsGlobal = ({
  isAdminTreeEnabled,
  selectedAdminArea,
  selectedAdminAreaIds,
  globalAdminAreaSettings,
  globalSelectedAreaIds
}: {
  isAdminTreeEnabled: boolean;
  selectedAdminArea: AdminAreaSettings | null;
  selectedAdminAreaIds: string[];
  globalAdminAreaSettings: AdminAreaSettings | null;
  globalSelectedAreaIds: string[];
}): boolean => {
  if (isAdminTreeEnabled) {
    if (globalSelectedAreaIds.length === 0) return false;
    if (globalSelectedAreaIds.length !== selectedAdminAreaIds.length) {
      return false;
    }
    const globalIdsSet = new Set(globalSelectedAreaIds);
    return selectedAdminAreaIds.every((id) => globalIdsSet.has(id));
  }

  if (!globalAdminAreaSettings?.lv1 || !selectedAdminArea?.lv1) return false;
  return (
    selectedAdminArea.lv1?.value === globalAdminAreaSettings.lv1?.value &&
    selectedAdminArea.lv2?.value === globalAdminAreaSettings.lv2?.value &&
    selectedAdminArea.lv3?.value === globalAdminAreaSettings.lv3?.value &&
    selectedAdminArea.lv4?.value === globalAdminAreaSettings.lv4?.value
  );
};

/**
 * Get `code` from the deepest level of selectedAdminArea.
 *
 * @example
 * const selectedAdminArea = {
 *   lv0: { value: "id", label: "Indonesia" },
 *   lv1: { label: "ACEH", value: "a2d4419d-...", code: "11", adm_unit: "province", isMatched: false },
 *   lv2: { label: "KABUPATEN ACEH BARAT", value: "xxx", code: "1101", adm_unit: "district", isMatched: false },
 * };
 *
 * getDeepestAdminAreaCode(selectedAdminArea); // "1101"
 */
export const getDeepestAdminAreaCode = (
  selectedAdminArea: AdminAreaSettings | null | undefined
): string[] => {
  if (!selectedAdminArea || typeof selectedAdminArea !== "object") {
    return [];
  }

  const keys = Object.keys(selectedAdminArea)
    .filter((key) => key.startsWith("lv"))
    .sort((a, b) => {
      const lvA = Number.parseInt(a.replace("lv", ""), 10);
      const lvB = Number.parseInt(b.replace("lv", ""), 10);
      return lvB - lvA;
    });

  for (const key of keys) {
    const entry = selectedAdminArea[key as keyof AdminAreaSettings];

    if (
      entry !== null &&
      entry !== undefined &&
      typeof entry === "object" &&
      "code" in entry &&
      typeof (entry as { code: string }).code === "string"
    ) {
      return [(entry as { code: string }).code];
    }
  }

  return [];
};
